import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { ResponsiveContainer, AreaChart, Area, CartesianGrid, XAxis, YAxis, Tooltip, PieChart as RechartsPie, Pie, Cell } from 'recharts';
import { formatCurrency } from '@/lib/utils';
import { DollarSign, Users, Target, CheckCircle2, ArrowUpRight, ArrowDownRight } from 'lucide-react';

const KPICard = ({ title, value, subtitle, icon: Icon, trend, trendValue, color = "teal" }) => (
  <Card className="relative overflow-hidden" data-testid={`kpi-${title.toLowerCase().replace(/\s+/g, '-')}`}>
    <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${color}-500 to-${color}-400`} />
    <CardContent className="pt-6">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
          {subtitle && <p className="text-xs text-muted-foreground">{subtitle}</p>}
          {trend && (
            <div className={`flex items-center gap-1 text-xs ${trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-slate-500'}`}>
              {trend === 'up' ? <ArrowUpRight className="w-3 h-3" /> : trend === 'down' ? <ArrowDownRight className="w-3 h-3" /> : null}
              <span>{trendValue}</span>
            </div>
          )}
        </div>
        <div className={`p-3 rounded-xl bg-${color}-50`}>
          <Icon className={`w-6 h-6 text-${color}-600`} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export { KPICard };

const segmentColors = {
  platinum: 'bg-slate-700',
  gold: 'bg-amber-500',
  silver: 'bg-slate-400',
  bronze: 'bg-orange-700'
};

export const OverviewTab = ({ loading, revenueData, clientData, operationsData, workerData }) => {
  if (loading) return <div className="text-center py-12">Loading analytics...</div>;

  return (
    <div className="space-y-6">
      {/* Top KPIs */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title="Total Revenue"
          value={formatCurrency(revenueData?.summary?.total_revenue || 0)}
          subtitle={`${revenueData?.summary?.invoice_count || 0} invoices`}
          icon={DollarSign}
          trend={revenueData?.growth?.growth_status}
          trendValue={`${revenueData?.growth?.growth_rate || 0}% vs prev period`}
          color="emerald"
        />
        <KPICard
          title="Active Clients"
          value={clientData?.summary?.active_clients || 0}
          subtitle={`${clientData?.summary?.new_this_month || 0} new this month`}
          icon={Users}
          trend={clientData?.acquisition?.growth > 0 ? 'up' : 'down'}
          trendValue={`${clientData?.acquisition?.growth || 0} vs last month`}
          color="blue"
        />
        <KPICard
          title="Collection Rate"
          value={`${revenueData?.summary?.collection_rate || 0}%`}
          subtitle={formatCurrency(revenueData?.summary?.total_collected || 0) + ' collected'}
          icon={Target}
          color="teal"
        />
        <KPICard
          title="SLA Compliance"
          value={`${operationsData?.processing?.sla_compliance || 0}%`}
          subtitle={`Target: ${operationsData?.processing?.sla_target_days || 3} days`}
          icon={CheckCircle2}
          color="violet"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Trend */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Revenue Trend</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={revenueData?.trend || []}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="hsl(188, 64%, 38%)" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="hsl(188, 64%, 38%)" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="period" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip formatter={(value) => formatCurrency(value)} />
                <Area type="monotone" dataKey="revenue" stroke="hsl(188, 64%, 38%)" fill="url(#colorRevenue)" strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Client Segmentation */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Client Segmentation</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex items-center justify-center h-[280px]">
              <ResponsiveContainer width="50%" height="100%">
                <RechartsPie>
                  <Pie
                    data={[
                      { name: 'Platinum', value: clientData?.segmentation?.platinum?.count || 0 },
                      { name: 'Gold', value: clientData?.segmentation?.gold?.count || 0 },
                      { name: 'Silver', value: clientData?.segmentation?.silver?.count || 0 },
                      { name: 'Bronze', value: clientData?.segmentation?.bronze?.count || 0 }
                    ]}
                    cx="50%"
                    cy="50%"
                    innerRadius={50}
                    outerRadius={80}
                    dataKey="value"
                  >
                    <Cell fill="#334155" />
                    <Cell fill="#f59e0b" />
                    <Cell fill="#94a3b8" />
                    <Cell fill="#c2410c" />
                  </Pie>
                  <Tooltip />
                </RechartsPie>
              </ResponsiveContainer>
              <div className="space-y-3">
                {['platinum', 'gold', 'silver', 'bronze'].map((seg) => (
                  <div key={seg} className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full ${segmentColors[seg]}`} />
                    <span className="text-sm capitalize">{seg}</span>
                    <Badge variant="secondary" className="ml-auto">
                      {clientData?.segmentation?.[seg]?.count || 0}
                    </Badge>
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-emerald-600">
              {formatCurrency(clientData?.lifetime_value?.average_ltv || 0)}
            </p>
            <p className="text-sm text-muted-foreground">Avg Client LTV</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-amber-600">
              {operationsData?.summary?.backlog || 0}
            </p>
            <p className="text-sm text-muted-foreground">Order Backlog</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-blue-600">
              {clientData?.retention?.retention_rate || 0}%
            </p>
            <p className="text-sm text-muted-foreground">Client Retention</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6 text-center">
            <p className="text-3xl font-bold text-violet-600">
              {workerData?.summary?.utilization_rate || 0}%
            </p>
            <p className="text-sm text-muted-foreground">Worker Utilization</p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
